import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { IndicatorNumber, TextInfoGroupAlt } from "@/components/shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { convertBpToDecimal, enumToClientText, formatNumberLocale, sortByShiftType } from "@/utils/helpers";

import { CarPurchasePriceDetails, PurchaseDetails, ShiftType } from "@/graphql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface PurchasesSectionProps {
  /** List of purchase details assigned to the service */
  purchases: PurchaseDetails[];
  /** Service-level additional price details used for purchase related breakdowns */
  carPurchasePriceDetails: CarPurchasePriceDetails[];
  /** Currently selected service id (used to pick the correct ratio link for each purchase) */
  serviceId: number;
}

/**
 * PurchasesSection - Renders an accordion section containing a list of collapsible purchase breakdown rows.
 * Each purchase row expands to show the purchase details and a type-grouped purchase-only additional pricing breakdown.
 *
 * @component
 * @example
 * <PurchasesSection purchases={service.purchases} carPurchasePriceDetails={service.pricing.carPurchasePriceDetails} serviceId={service.id} />
 */
function PurchasesSection({ purchases, carPurchasePriceDetails, serviceId }: PurchasesSectionProps) {
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common"]);
  const [openByPurchaseId, setOpenByPurchaseId] = useState<Record<number, boolean>>({});

  const sortedCarPurchasePriceDetails = useMemo(() => {
    return sortByShiftType(carPurchasePriceDetails ?? []);
  }, [carPurchasePriceDetails]);

  const hasPurchases = (purchases?.length ?? 0) > 0;

  return (
    <Card className="shadow-none">
      <Accordion type="single" collapsible>
        <AccordionItem value="purchases">
          <AccordionTrigger className="hover:no-underline hover:bg-slate-50 px-4 py-4">
            <div className="flex items-center gap-2 w-full">
              <div className="text-sm font-bold grow">{t("edittender.calculationoverview.components.purchases.title")}</div>
              <IndicatorNumber number={purchases.length} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-2 pb-4">
            {!hasPurchases ? (
              <div className="text-sm text-slate-500 text-center pt-4">
                {t("edittender.calculationoverview.components.purchases.empty")}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {purchases.map((purchaseDetails) => {
                  const purchase = purchaseDetails.purchase;
                  if (!purchase) return null;

                  const ratioBp = purchaseDetails.links?.find((link) => link.serviceId === serviceId)?.ratio;
                  const ratioPercent = ratioBp === null || ratioBp === undefined ? undefined : convertBpToDecimal(ratioBp);

                  const isOpen = openByPurchaseId[purchase.id] ?? false;
                  const purchaseName = purchase.name || t("edittender.calculationoverview.components.purchases.unnamed");

                  const purchaseDetailsMetrics = [
                    {
                      key: "quantity",
                      label: t("edittender.calculationoverview.common.metrics.quantity"),
                      value: formatNumberLocale(intl, purchase.quantity ?? 0, "decimal", false, true),
                    },
                    {
                      key: "monthsdepreciation",
                      label: t("edittender.calculationoverview.components.purchases.labels.monthsdepreciation"),
                      value: formatNumberLocale(intl, purchase.monthsDepreciation ?? 0, "decimal", false, true),
                    },
                    {
                      key: "purchasecost",
                      label: t("edittender.calculationoverview.components.purchases.labels.purchasecost"),
                      value: formatNumberLocale(intl, purchase.purchaseCost ?? 0, "currency"),
                    },
                    {
                      key: "yearlymaintenance",
                      label: t("edittender.calculationoverview.components.purchases.labels.yearlymaintenance"),
                      value: formatNumberLocale(intl, purchase.yearlyMaintenance ?? 0, "currency"),
                    },
                    {
                      key: "yearlyrepair",
                      label: t("edittender.calculationoverview.components.purchases.labels.yearlyrepair"),
                      value: formatNumberLocale(intl, purchase.yearlyRepair ?? 0, "currency"),
                    },
                    {
                      key: "yearlyadditionals",
                      label: t("edittender.calculationoverview.components.purchases.labels.yearlyadditionals"),
                      value: formatNumberLocale(intl, purchase.yearlyAdditionals ?? 0, "currency"),
                    },
                    {
                      key: "revenue",
                      label: t("common:pricing.labels.revenue"),
                      value: formatNumberLocale(intl, purchase.revenue ?? 0, "currency"),
                    },
                    {
                      key: "totalcost",
                      label: t("edittender.calculationoverview.common.metrics.totalcost"),
                      value: formatNumberLocale(intl, purchase.totalCost ?? 0, "currency"),
                    },
                    {
                      key: "margin",
                      label: t("common:pricing.labels.margin"),
                      value: `${formatNumberLocale(intl, convertBpToDecimal(purchase.margin ?? 0), "decimal", false, false, 0, 2)}%`,
                    },
                  ];

                  return (
                    <Card key={`purchase-collapsible-${purchase.id}`} className="shadow-none">
                      <Collapsible
                        open={isOpen}
                        onOpenChange={(open) => setOpenByPurchaseId((prev) => ({ ...prev, [purchase.id]: open }))}
                      >
                        <CollapsibleTrigger asChild>
                          <CardHeader className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2 grow justify-between">
                                <span className="text-sm font-semibold break-all">{purchaseName}</span>
                                <Badge variant="secondary">
                                  {t("edittender.calculationoverview.common.metrics.ratio")}: &nbsp;
                                  {ratioPercent === undefined ? "—" : `${formatNumberLocale(intl, ratioPercent, "decimal", false, false, 0, 2)}%`}
                                </Badge>
                              </div>
                              {isOpen ? (
                                <ChevronUpIcon className="size-4 shrink-0 text-slate-500" />
                              ) : (
                                <ChevronDownIcon className="size-4 shrink-0 text-slate-500" />
                              )}
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <CardContent className="px-4 pb-4 pt-0">
                            <Separator className="mb-4" />

                            {/* Purchase card (based on example data) */}
                            <Card className="shadow-none p-2">
                              <CardHeader className="p-0 pb-3">
                                <CardTitle className="text-sm font-bold">
                                  {t("edittender.calculationoverview.components.purchases.cardtitle")}
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="p-0">
                                <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                                  {purchaseDetailsMetrics.map((metric) => (
                                    <TextInfoGroupAlt
                                      key={metric.key}
                                      label={metric.label}
                                      content={metric.value}
                                      hasBackground={true}
                                      backgroundColor="slate"
                                    />
                                  ))}
                                </div>
                              </CardContent>
                            </Card>

                            <Separator className="my-4" />

                            {/* Additional price details (purchase-only fields grouped by type) */}
                            <div className="flex flex-col gap-2">
                              <div className="text-sm font-bold">
                                {t("edittender.calculationoverview.components.purchases.additionalbreakdown")}
                              </div>

                              {sortedCarPurchasePriceDetails.map((detail) => {
                                const typeLabel = enumToClientText(detail.type);

                                return (
                                  <div key={`purchase-additional-${detail.type}`}>
                                    <div
                                      className={cn(
                                        "flex flex-wrap items-center rounded-md px-2 py-1",
                                        detail.type === ShiftType.Cumulated ? "bg-slate-50 hover:bg-slate-100" : "hover:bg-slate-50"
                                      )}
                                    >
                                      <div className="mb-1 w-1/4 font-semibold">
                                        {t(`common:costdetailstype.${detail.type.toLowerCase()}`) || typeLabel}
                                      </div>
                                      <div className="flex w-3/4 flex-wrap gap-x-4 gap-y-2">
                                        <TextInfoGroupAlt
                                          className="w-[150px]"
                                          label={t("common:pricing.labels.costs")}
                                          content={formatNumberLocale(intl, detail.purchaseCosts ?? 0, "currency")}
                                        />
                                        <TextInfoGroupAlt
                                          className="w-[150px]"
                                          label={t("common:pricing.labels.revenue")}
                                          content={formatNumberLocale(intl, detail.purchaseRevenue ?? 0, "currency")}
                                        />
                                      </div>
                                    </div>
                                    <Separator className="my-2" />
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  );
                })}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default PurchasesSection;
